import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Pencil, Printer, ShieldAlert, Sparkles } from 'lucide-react';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Button } from '@/shared/ui/Button';
import { useBrandKit } from '@/shared/context/useBrandKit';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from './LetterheadPage.module.css';

interface CollateralItem {
  name: string;
  description: string;
}

const COLLATERAL: CollateralItem[] = [
  {
    name: 'Treatment brochure',
    description: '"What to expect with dental implants" — from your services, in your warm voice.',
  },
  {
    name: 'Welcome folder',
    description: 'New-patient welcome copy + what makes the practice different.',
  },
  {
    name: 'Letterhead',
    description: 'Header/footer locked to your Brand Kit logo, palette, contact details.',
  },
  {
    name: 'Appointment cards',
    description: 'On-brand reminders — "See you soon, we\'ll take good care of you."',
  },
  {
    name: 'Business cards',
    description: 'Dr. Jain + team cards from the approved identity.',
  },
  {
    name: 'Window / wall banner',
    description: 'Large-format, brand-consistent, for the office exterior.',
  },
];

export function LetterheadPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const navigate = useNavigate();
  const { approved, kitName } = useBrandKit();

  return (
    <div className={patterns.page}>
      <PageHeader
        title="Letterhead & Print"
        subtitle="Brand-consistent collateral with AI-written copy. Approve your Brand Kit, then preview, customize, and order."
      />

      {approved ? (
        <div className={styles.brandKitBannerApproved}>
          <div className={styles.bannerLeftMuted}>
            <span className={styles.bannerMark}>◎</span>
            Using approved Brand Kit: <strong>{kitName}</strong>
          </div>
          <button className={styles.manageLink} onClick={() => navigate('/strategy/brand-kit')}>
            Manage in Strategy ▸ Brand Kit <ArrowRight size={12} aria-hidden />
          </button>
        </div>
      ) : (
        <div className={styles.brandKitBanner}>
          <div className={styles.bannerLeft}>
            <ShieldAlert size={15} aria-hidden />
            Approve your Brand Kit first so your printed pieces stay on-brand.
          </div>
          <Button
            variant="secondary"
            size="sm"
            rightIcon={<ArrowRight size={13} aria-hidden />}
            onClick={() => navigate('/strategy/brand-kit')}
          >
            Go to Brand Kit
          </Button>
        </div>
      )}

      <div className={`${styles.grid} ${!approved ? styles.lockedOverlay : ''}`}>
        {COLLATERAL.map((item, i) => (
          <div key={item.name} className={`${patterns.panelCompact} ${styles.collateralCard}`}>
            <div className={styles.preview}>
              <div className={styles.previewMark}>◎</div>
              <div className={styles.previewBrand}>MT. DIABLO FAMILY DENTISTS</div>
            </div>

            <div className={styles.collateralName}>{item.name}</div>
            <div className={styles.collateralDesc}>
              <span className={styles.descIcon}>
                <Sparkles size={11} aria-hidden />
              </span>
              {item.description}
            </div>

            {openIndex === i ? (
              <div className={styles.orderPanel}>
                <div className={styles.orderTitle}>Order — 3 steps</div>
                <div className={styles.orderStep}>
                  <span className={styles.orderStepIconDone}>
                    <Check size={12} aria-hidden />
                  </span>
                  1. Preview generated
                </div>
                <div className={styles.orderStep}>
                  <span className={styles.orderStepIconPending}>
                    <Pencil size={12} aria-hidden />
                  </span>
                  2. Customize copy / qty
                </div>
                <div className={styles.orderStep}>
                  <span className={styles.orderStepIconPending}>
                    <Printer size={12} aria-hidden />
                  </span>
                  3. Order via partner (at cost)
                </div>
                <div className={styles.orderActions}>
                  <Button size="sm" leftIcon={<Printer size={13} aria-hidden />}>
                    Place order
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => setOpenIndex(null)}>
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              <div className={styles.collateralActions}>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Pencil size={13} aria-hidden />}
                  onClick={() => setOpenIndex(i)}
                >
                  Customize
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Printer size={13} aria-hidden />}
                  onClick={() => setOpenIndex(i)}
                >
                  Order
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
